import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import './TaskForm.css';

function TaskForm({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    assigned_users: []
  });
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    if (isEdit) {
      fetchTask();
    }
  }, [id]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTask = async () => {
    try {
      const response = await axios.get(`/tasks/${id}`);
      const task = response.data.task;
      setFormData({
        title: task.title,
        description: task.description || '',
        due_date: task.due_date,
        priority: task.priority,
        assigned_users: task.assigned_users.map(u => u.id)
      });
    } catch (error) {
      console.error('Error fetching task:', error);
      setError('Σφάλμα κατά τη φόρτωση της υποχρέωσης');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUserSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 1) {
      try {
        const response = await axios.get('/users/search', {
          params: { query }
        });
        setSearchResults(response.data.users);
      } catch (error) {
        console.error('Error searching users:', error);
      }
    } else {
      setSearchResults([]);
    }
  };

  const addUser = (userId) => {
    if (!formData.assigned_users.includes(userId)) {
      setFormData({
        ...formData,
        assigned_users: [...formData.assigned_users, userId]
      });
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeUser = (userId) => {
    setFormData({
      ...formData,
      assigned_users: formData.assigned_users.filter(id => id !== userId)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`/tasks/${id}`, formData);
      } else {
        await axios.post('/tasks', formData);
      }
      navigate('/tasks');
    } catch (err) {
      setError(err.response?.data?.error || 'Σφάλμα κατά την αποθήκευση');
      setLoading(false);
    }
  };

  const getAssignedUsers = () => {
    return users.filter(u => formData.assigned_users.includes(u.id));
  };

  return (
    <div>
      <Header user={user} onLogout={onLogout} />
      
      <div className="form-container">
        <h1>{isEdit ? 'Επεξεργασία Υποχρέωσης' : 'Νέα Υποχρέωση'}</h1>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label htmlFor="title">Τίτλος *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="π.χ. Ολοκλήρωση αναφοράς"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Περιγραφή</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Προαιρετική περιγραφή της υποχρέωσης..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="due_date">Προθεσμία *</label>
              <input
                type="date"
                id="due_date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="priority">Προτεραιότητα</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="low">Χαμηλή</option>
                <option value="medium">Μεσαία</option>
                <option value="high">Υψηλή</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Ανάθεση σε Χρήστες</label>
            <input
              type="text"
              value={searchQuery}
              onChange={handleUserSearch}
              placeholder="Αναζήτηση χρηστών..."
              className="user-search"
            />
            
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map(u => (
                  <div
                    key={u.id}
                    className="search-result-item"
                    onClick={() => addUser(u.id)}
                  >
                    {u.full_name} ({u.email})
                  </div>
                ))}
              </div>
            )}

            <div className="assigned-users">
              {getAssignedUsers().map(u => (
                <div key={u.id} className="user-tag">
                  <span>{u.full_name}</span>
                  <button
                    type="button"
                    onClick={() => removeUser(u.id)}
                    className="remove-user"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Αποθήκευση...' : (isEdit ? 'Ενημέρωση' : 'Δημιουργία')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/tasks')}
              className="btn-secondary"
            >
              Ακύρωση
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TaskForm;
